import { useEffect, useState } from 'react';
import { Mission } from '../../data/missions';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import FileDrop from '../FileDrop/FileDrop';
import MissionShell, { ErrorNotice, InfoNotice, SuccessNotice } from '../MissionShell/MissionShell';
import {
  buildFixedAssets,
  FixedAssetsInspection,
  FixedAssetsSummary,
  inspectFixedAssets,
} from '../../api/automationApi';
import { useFileRun } from '../../hooks/useFileRun';
import type { FileResult } from '../../api/files';
import { ProcessingNotice } from '@/components/ui/processing-notice';

const STEPS = [
  'Upload the fixed asset workbook (each sheet may hold several tables; summary tables are skipped).',
  'For every output column, pick the heading from your file that belongs in it. Matching headings are pre-selected.',
  'Click Generate register. Every account name is followed by its transactions.',
];

const NONE = '';

const selectClass =
  'h-9 w-full rounded-md border border-input bg-background px-3 text-sm text-foreground';

interface PanelProps {
  mission: Mission;
  onBack: () => void;
}

function FixedAssetsPanel({ mission, onBack }: PanelProps) {
  const [files, setFiles] = useState<File[]>([]);
  const [inspection, setInspection] = useState<FixedAssetsInspection | null>(null);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [yearLabels, setYearLabels] = useState<Record<string, string>>({});
  const [reading, setReading] = useState(false);
  const { status, error, result, fail, reset, run } = useFileRun<FileResult<FixedAssetsSummary>>();

  const file = files[0];

  // Re-read the workbook whenever a new file is chosen.
  useEffect(() => {
    setInspection(null);
    reset();
    if (!file) return;
    let cancelled = false;
    setReading(true);
    inspectFixedAssets(file)
      .then((data) => {
        if (cancelled) return;
        setInspection(data);
        const suggested: Record<string, string> = {};
        Object.entries(data.suggested).forEach(([name, heading]) => {
          suggested[name] = heading ?? NONE;
        });
        setMapping(suggested);
        setYearLabels({ ...data.year_defaults });
      })
      .catch((err) => {
        if (cancelled) return;
        fail(err instanceof Error ? err.message : 'Could not read the workbook.');
      })
      .finally(() => !cancelled && setReading(false));
    return () => {
      cancelled = true;
    };
  }, [file, reset, fail]);

  const handleSubmit = async () => {
    if (!file || !inspection) return;
    const chosen: Record<string, string | null> = {};
    Object.entries(mapping).forEach(([name, heading]) => {
      chosen[name] = heading || null;
    });
    if (!Object.values(chosen).some(Boolean)) {
      fail('Map at least one column.');
      return;
    }
    await run(() => buildFixedAssets(file, chosen, yearLabels), 'Building the register failed.');
  };

  return (
    <MissionShell mission={mission} onBack={onBack} steps={STEPS} wide>
      <FileDrop
        label="Fixed asset workbook"
        required
        accept={mission.accept}
        hint={mission.maxSizeLabel}
        accentColor={mission.color}
        files={files}
        onFiles={setFiles}
      />

      {reading && <InfoNotice>Reading the workbook…</InfoNotice>}

      {inspection && (
        <>
          <p className="mb-3 text-xs text-muted-foreground">
            Found {inspection.tables} table(s) with {inspection.rows} rows and {inspection.headings.length}{' '}
            distinct headings. Left: the output column. Right: the heading from your file to place in it.
          </p>
          <div className="mb-6 space-y-2 rounded-lg border border-border bg-muted p-4">
            {inspection.columns.map((col) => (
              <div key={col.name} className="grid items-center gap-2 sm:grid-cols-[200px_1fr]">
                <span className="text-sm font-semibold text-foreground">{col.name}</span>
                <div className="space-y-1.5">
                  <select
                    className={selectClass}
                    aria-label={col.name}
                    value={mapping[col.name] ?? NONE}
                    onChange={(e) => setMapping((m) => ({ ...m, [col.name]: e.target.value }))}
                  >
                    <option value={NONE}>— none —</option>
                    {inspection.headings.map((h) => (
                      <option key={h} value={h}>
                        {h}
                      </option>
                    ))}
                  </select>
                  {col.is_year && mapping[col.name] && (
                    <Input
                      value={yearLabels[col.name] ?? ''}
                      onChange={(e) => setYearLabels((y) => ({ ...y, [col.name]: e.target.value }))}
                      placeholder={`Year shown above ${col.name}, e.g. 2022`}
                      aria-label={`Year shown above ${col.name}`}
                    />
                  )}
                </div>
              </div>
            ))}
          </div>

          <Button
            onClick={handleSubmit}
            disabled={status === 'loading'}
            style={{ backgroundColor: mission.color }}
            className="w-full text-white hover:opacity-90"
          >
            {status === 'loading' ? 'Building…' : 'Generate register'}
          </Button>
          {status === 'loading' && <ProcessingNotice label={'Building…'} />}
        </>
      )}

      {status === 'error' && error && <ErrorNotice message={error} />}
      {status === 'success' && result && (
        <SuccessNotice>
          <p>✅ {result.filename} downloaded.</p>
          {result.summary && (
            <p className="mt-1 text-xs text-green-800">
              {result.summary.tables} table(s) · {result.summary.rows} rows · {result.summary.columns} mapped
              column(s).
            </p>
          )}
        </SuccessNotice>
      )}
      {!file && status === 'idle' && <InfoNotice>📁 Upload a workbook to begin.</InfoNotice>}
    </MissionShell>
  );
}

export default FixedAssetsPanel;
